import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import appAPI from '../../api/api'

type CaptchaState = {
  captcha: Captcha | undefined
  loading: boolean
}

const initialCaptchaState: CaptchaState = {
  captcha: undefined,
  loading: false,
}

export const fetchCaptcha = createAsyncThunk('fetchCaptcha', async () => {
  return await appAPI.captcha.getCaptcha()
})

const normalizeCaptchaResponse = (payload: Captcha | Captcha[]): Captcha => {
  return Array.isArray(payload) ? payload[0] : payload
}

const getApiOrigin = (): string | undefined => {
  const endpoint = process.env.REACT_APP_ENDPOINT
  if (!endpoint) {
    return undefined
  }

  try {
    return new URL(endpoint).origin
  } catch {
    return undefined
  }
}

const getCaptchaImageSrc = (captcha: Captcha): string => {
  if (typeof captcha.captchaImage === 'string') {
    const apiOrigin = getApiOrigin()

    if (!apiOrigin) {
      return captcha.captchaImage
    }

    try {
      const imageUrl = new URL(captcha.captchaImage, apiOrigin)
      return `${apiOrigin}${imageUrl.pathname}${imageUrl.search}`
    } catch {
      return captcha.captchaImage
    }
  }

  return (
    `data:${captcha.imageType};base64,` +
    String.fromCharCode.apply(null, Array.from(new Uint8Array(captcha.captchaImage).values()) as number[])
  )
}

const captchaSlice = createSlice({
  name: 'captcha',
  initialState: initialCaptchaState,
  reducers: {
    clear: () => {
      return initialCaptchaState
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCaptcha.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchCaptcha.fulfilled, (state, { payload }) => {
        state.loading = false
        state.captcha = normalizeCaptchaResponse(payload.data)
        state.captcha.captchaBase64 = getCaptchaImageSrc(state.captcha)
      })
      .addCase(fetchCaptcha.rejected, (state) => {
        state.loading = false
        state.captcha = initialCaptchaState.captcha
      })
  },
})

export const captchaActions = {
  fetchCaptcha,
  ...captchaSlice.actions,
}

export default captchaSlice.reducer
